import { NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Params, RouterLink, RouterLinkActive, UrlTree } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { RtAnalyticsModule } from '@recruitee/analytics';

import { PreventDragDropDirective } from '../../common/drag-and-drop/prevent-dragdrop.directive';
import { IconColorDirective } from '../../common/icon/directives/icon-color.directive';
import { IconComponent, IconSize } from '../../common/icon/icon.component';
import { RtTooltip } from '../../common/tooltip/tooltip.directive';
import { OverlayOriginDirective } from '../../core/overlay/overlay-directives';
import { IconName } from '../../icons-container/icons.type';
import { SidebarInternalService } from '../sidebar/sidebar-internal.service';

export enum ItemType {
  Heading = 'heading',
  Item = 'item',
}

export enum SidebarItemSize {
  Small,
  Default,
  Large,
}

type TItemType = `${ItemType}`;

@Component({
  selector: 'rt-sidebar-item',
  templateUrl: './sidebar-item.component.html',
  styleUrls: ['./sidebar-item.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.heading]': 'type === ItemType.Heading',
    '[class.item]': 'type === ItemType.Item',
    '[class.default]': 'itemSize === SidebarItemSize.Default',
    '[class.small]': 'itemSize === SidebarItemSize.Small',
    '[class.large]': 'itemSize === SidebarItemSize.Large',
  },
  standalone: true,
  imports: [
    IconColorDirective,
    IconComponent,
    NgIf,
    NgTemplateOutlet,
    OverlayOriginDirective,
    PreventDragDropDirective,
    RouterLink,
    RouterLinkActive,
    RtAnalyticsModule,
    RtTooltip,
  ],
})
export class SidebarItemComponent implements OnInit, OnDestroy {
  @Input() public type: TItemType;
  @Input() public name: string;
  @Input() public icon?: IconName;
  @Input() public active: boolean;
  @Input() public iconActive: boolean;
  @Input() public rightOutlet?: TemplateRef<any> | null;
  @Input() public rightOutletContext?: Record<string, any> | null;
  @Input() public bottomOutlet?: TemplateRef<any> | null;
  @Input() public bottomOutletContext: Record<string, any> | null;
  @Input() public draggable: boolean;
  @Input() public dragIconSize: IconSize = 'small';
  @Input() public light: boolean;
  @Input() public itemSize: SidebarItemSize = SidebarItemSize.Default;
  @Input() public hoverable: boolean = true;
  @Input() public tooltipEnabled: boolean = true;
  @Input() public activeStateIndicatorEnabled: boolean = true;
  @Input() public analyticsEvent: string;
  @Input() public analyticsEventData: any;

  @Input()
  public set href(value: UrlTree | string | undefined | null) {
    if (value instanceof UrlTree) {
      this.url = value.toString().split('?')[0];
      this.queryParams = value.queryParams;
    } else {
      this.url = value;
    }
  }

  @ViewChild('itemName') public itemNameRef: ElementRef<HTMLDivElement>;
  @ViewChild('rightOutletOrigin')
  public rightOutletOrigin: OverlayOriginDirective;

  public ItemType: typeof ItemType = ItemType;
  public SidebarItemSize: typeof SidebarItemSize = SidebarItemSize;
  public sidebarMinimized: boolean;
  public url?: string | null;
  public queryParams: Params;
  public isTooltipEnabledFn: () => boolean = ((): boolean => {
    if (!this.itemNameRef) return false;

    const { nativeElement } = this.itemNameRef;
    return nativeElement.scrollWidth > nativeElement.clientWidth;
  }).bind(this);

  private ngOnDestroy$ = new Subject<void>();

  constructor(private cdRef: ChangeDetectorRef, private sidebarService: SidebarInternalService) {}

  public ngOnInit(): void {
    this.sidebarService.sidebarMinimized$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(isMinimized => {
        this.sidebarMinimized = isMinimized;
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
